import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  BarChart3,
  TrendingUp,
  Calendar,
  Download,
  Filter,
  Activity,
  Target,
  Clock,
  Flame
} from 'lucide-react'

const Reports = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month')
  const [selectedReport, setSelectedReport] = useState('overview')

  const reportTypes = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    { id: 'workouts', name: 'Workouts', icon: Activity },
    { id: 'nutrition', name: 'Nutrition', icon: Target },
    { id: 'progress', name: 'Progress', icon: TrendingUp }
  ]

  const periods = [
    { id: 'week', name: 'This Week' },
    { id: 'month', name: 'This Month' },
    { id: '3months', name: 'Last 3 Months' },
    { id: 'year', name: 'This Year' }
  ]

  // Mock data
  const summaryStats = {
    totalWorkouts: 24,
    totalDuration: 1080, // minutes
    avgCaloriesBurned: 320,
    consistencyRate: 85,
    favoriteWorkout: 'Full Body Strength',
    avgNutritionScore: 78
  }

  const weeklyData = [
    { week: 'Week 1', workouts: 4, calories: 1280, nutrition: 85 },
    { week: 'Week 2', workouts: 5, calories: 1600, nutrition: 78 },
    { week: 'Week 3', workouts: 6, calories: 1920, nutrition: 82 },
    { week: 'Week 4', workouts: 5, calories: 1600, nutrition: 88 }
  ]

  return (
    <div className="app-page">
      <div className="app-page-header">
        <div>
          <h1 className="app-page-heading">Reports & Analytics</h1>
          <p className="app-page-subheading">Detailed insights into your fitness journey</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
          <Button variant="fitness">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Report Type Selection */}
      <div className="flex flex-wrap gap-2">
        {reportTypes.map((type) => {
          const Icon = type.icon
          return (
            <Button
              key={type.id}
              variant={selectedReport === type.id ? "selected" : "outline"}
              onClick={() => setSelectedReport(type.id)}
            >
              <Icon className="h-4 w-4 mr-2" />
              {type.name}
            </Button>
          )
        })}
      </div>

      {/* Period Selection */}
      <div className="flex flex-wrap gap-2">
        {periods.map((period) => (
          <Button
            key={period.id}
            variant={selectedPeriod === period.id ? "secondary" : "outline"}
            size="sm"
            onClick={() => setSelectedPeriod(period.id)}
          >
            {period.name}
          </Button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardContent className="p-6 text-center">
              <Activity className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <div className="mb-1 text-2xl font-semibold text-slate-900">
                {summaryStats.totalWorkouts}
              </div>
              <div className="text-sm text-slate-500">Total Workouts</div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardContent className="p-6 text-center">
              <Clock className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <div className="mb-1 text-2xl font-semibold text-slate-900">
                {Math.round(summaryStats.totalDuration / 60)}h
              </div>
              <div className="text-sm text-slate-500">Total Duration</div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardContent className="p-6 text-center">
              <Flame className="h-8 w-8 text-orange-600 mx-auto mb-2" />
              <div className="mb-1 text-2xl font-semibold text-slate-900">
                {summaryStats.avgCaloriesBurned}
              </div>
              <div className="text-sm text-slate-500">Avg Calories/Workout</div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardContent className="p-6 text-center">
              <Target className="h-8 w-8 text-purple-600 mx-auto mb-2" />
              <div className="mb-1 text-2xl font-semibold text-slate-900">
                {summaryStats.consistencyRate}%
              </div>
              <div className="text-sm text-slate-500">Consistency Rate</div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card>
            <CardContent className="p-6 text-center">
              <BarChart3 className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <div className="mb-1 text-2xl font-semibold text-slate-900">
                {summaryStats.avgNutritionScore}
              </div>
              <div className="text-sm text-slate-500">Nutrition Score</div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card>
            <CardContent className="p-6 text-center">
              <TrendingUp className="h-8 w-8 text-pink-600 mx-auto mb-2" />
              <div className="mb-1 text-lg font-semibold text-slate-900">
                Strength
              </div>
              <div className="text-sm text-slate-500">Top Category</div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Chart */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5 text-blue-600" />
                <span>Performance Trends</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Mock chart area */}
              <div className="flex h-80 items-center justify-center rounded-lg bg-slate-50">
                <div className="text-center text-slate-400">
                  <BarChart3 className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">Interactive Chart</p>
                  <p className="text-sm">Performance trends would be displayed here</p>
                  <p className="text-xs mt-2">Integration with Chart.js or similar library needed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Weekly Breakdown */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-green-600" />
                <span>Weekly Breakdown</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {weeklyData.map((week, index) => (
                  <div key={week.week} className="border-b border-slate-100 pb-4 last:border-b-0">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-slate-900">{week.week}</span>
                      <span className="text-sm text-slate-400">{week.nutrition}% nutrition</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Workouts</span>
                        <span className="font-medium">{week.workouts}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Calories</span>
                        <span className="font-medium">{week.calories}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Detailed Tables */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="px-4 py-3 text-left font-medium text-slate-900">Date</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-900">Workout</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-900">Duration</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-900">Calories</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-900">Nutrition</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { date: '2024-01-15', workout: 'Full Body Strength', duration: '45 min', calories: 320, nutrition: '85%' },
                  { date: '2024-01-14', workout: 'HIIT Cardio', duration: '30 min', calories: 280, nutrition: '78%' },
                  { date: '2024-01-13', workout: 'Yoga Flow', duration: '60 min', calories: 180, nutrition: '92%' },
                  { date: '2024-01-12', workout: 'Upper Body', duration: '40 min', calories: 290, nutrition: '80%' },
                  { date: '2024-01-11', workout: 'Running', duration: '35 min', calories: 350, nutrition: '75%' }
                ].map((row, index) => (
                  <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-900">{row.date}</td>
                    <td className="px-4 py-3 text-slate-900">{row.workout}</td>
                    <td className="px-4 py-3 text-slate-500">{row.duration}</td>
                    <td className="px-4 py-3 text-slate-500">{row.calories}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        parseInt(row.nutrition) >= 85 ? 'bg-green-100 text-green-700' :
                        parseInt(row.nutrition) >= 75 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {row.nutrition}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default Reports
